import React from 'react';
import {
  Box,
  Button,
  Dialog,
  Field,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Project } from '../types';

interface ProjectStageDialogsProps {
  project: Project;
  stageDialogOpen: boolean;
  closeStageDialog: () => void;
  deleteDialogOpen: boolean;
  onDeleteDialogClose: () => void;
  newStageTitle: string;
  setNewStageTitle: (v: string) => void;
  newStageColor: string;
  setNewStageColor: (v: string) => void;
  newStageSetAsFinished: boolean;
  setNewStageSetAsFinished: (v: boolean) => void;
  handleAddStage: () => void;
  confirmDeleteStage: () => void;
  addButtonLabel?: string;
}

const ProjectStageDialogs: React.FC<ProjectStageDialogsProps> = ({
  project,
  stageDialogOpen,
  closeStageDialog,
  deleteDialogOpen,
  onDeleteDialogClose,
  newStageTitle,
  setNewStageTitle,
  newStageColor,
  setNewStageColor,
  newStageSetAsFinished,
  setNewStageSetAsFinished,
  handleAddStage,
  confirmDeleteStage,
  addButtonLabel = 'Add Stage',
}) => (
  <>
    <Dialog.Root open={stageDialogOpen} onOpenChange={(e: { open: boolean }) => !e.open && closeStageDialog()}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Add New Stage</Dialog.Title>
            <Dialog.CloseTrigger />
          </Dialog.Header>
          <Dialog.Body>
            <VStack gap={4}>
              <Field.Root required>
                <Field.Label>Stage title</Field.Label>
                <Input
                  value={newStageTitle}
                  onChange={(e) => setNewStageTitle(e.target.value)}
                  placeholder="e.g., Review, Testing"
                  autoFocus
                />
              </Field.Root>
              {!project.finishedColumnId && (
                <Field.Root>
                  <Field.Label>
                    <HStack gap={2} as="span">
                      <Box as="span" display="inline-flex" color="gold">★</Box>
                      Stage for finished tasks
                    </HStack>
                  </Field.Label>
                  <HStack gap={2}>
                    <input
                      type="checkbox"
                      id="setAsFinishedStage"
                      checked={newStageSetAsFinished}
                      onChange={(e) => setNewStageSetAsFinished(e.target.checked)}
                    />
                    <label htmlFor="setAsFinishedStage" style={{ fontSize: '14px', color: '#718096', cursor: 'pointer' }}>
                      Only one stage can be marked. Tasks in it will not be suggested as dependencies.
                    </label>
                  </HStack>
                </Field.Root>
              )}
              <Field.Root>
                <Field.Label>Color</Field.Label>
                <HStack gap={3} align="center">
                  <Box
                    w="60px"
                    h="40px"
                    borderRadius="md"
                    borderWidth={2}
                    borderColor="gray.300"
                    bg={newStageColor}
                    cursor="pointer"
                    position="relative"
                    overflow="hidden"
                  >
                    <input
                      type="color"
                      value={newStageColor}
                      onChange={(e) => setNewStageColor(e.target.value)}
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                  <Input
                    value={newStageColor}
                    onChange={(e) => setNewStageColor(e.target.value)}
                    placeholder="#9CA3AF"
                    maxW="120px"
                    fontFamily="mono"
                  />
                </HStack>
              </Field.Root>
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <HStack>
              <Button variant="ghost" onClick={closeStageDialog}>
                Cancel
              </Button>
              <Button
                bgGradient="to-r"
                gradientFrom="#273f96"
                gradientTo="#1369b3"
                color="white"
                onClick={handleAddStage}
              >
                {addButtonLabel}
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>

    <Dialog.Root open={deleteDialogOpen} onOpenChange={(e: { open: boolean }) => !e.open && onDeleteDialogClose()}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Delete Stage</Dialog.Title>
            <Dialog.CloseTrigger />
          </Dialog.Header>
          <Dialog.Body>
            <VStack gap={2}>
              <Text>
                Are you sure you want to delete this stage? Move or delete tasks in it first.
              </Text>
              <Text fontSize="sm" color="gray.600">
                This action cannot be undone.
              </Text>
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <HStack>
              <Button variant="ghost" onClick={onDeleteDialogClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteStage}>
                Delete
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  </>
);

export default ProjectStageDialogs;
